import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  Sheet,
  Tooltip,
} from '@mui/joy';
import {
  Plus as AddIcon,
  Sparkle as AutoAwesomeIcon,
  Trash as DeleteIcon,
  Globe as LanguageIcon,
  ArrowSquareOut as OpenInNewIcon,
} from '@phosphor-icons/react';
import api from '../../../lib/api';
import type { WebsiteProject, WebsiteProjectStatusValue } from '../../../types/websiteBuilder';

const statusColors: Record<WebsiteProjectStatusValue, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  questionnaire: 'neutral',
  generating_design_system: 'primary',
  generating_homepage: 'primary',
  homepage_review: 'warning',
  generating_site: 'primary',
  generating_content: 'primary',
  assembling: 'primary',
  site_review: 'warning',
  deploying: 'primary',
  deployed: 'success',
  failed: 'danger',
};

const statusLabels: Record<WebsiteProjectStatusValue, string> = {
  questionnaire: 'Draft',
  generating_design_system: 'Generating Design...',
  generating_homepage: 'Generating Homepage...',
  homepage_review: 'Review Homepage',
  generating_site: 'Generating Site...',
  generating_content: 'Generating Content...',
  assembling: 'Assembling Pages...',
  site_review: 'Review Site',
  deploying: 'Deploying...',
  deployed: 'Live',
  failed: 'Failed',
};

export const WebsiteBuilderList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: projects, isLoading } = useQuery({
    queryKey: ['website-builder-projects'],
    queryFn: async () => {
      const response = await api.websiteBuilder.listProjects();
      return response.data;
    },
  });

  const deleteProject = useMutation({
    mutationFn: async (projectId: number) => {
      await api.websiteBuilder.deleteProject(projectId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-builder-projects'] });
    },
  });

  const handleDelete = (e: React.MouseEvent, projectId: number, projectName: string) => {
    e.stopPropagation();
    if (
      window.confirm(
        `Delete "${projectName}"? If this website is published, its live site, ` +
        `hosting, and web address are permanently removed too. This cannot be undone.`,
      )
    ) {
      deleteProject.mutate(projectId);
    }
  };

  // Product cap: up to 2 websites per account (enforced server-side too).
  const MAX_WEBSITES = 2;
  const atLimit = (projects?.length ?? 0) >= MAX_WEBSITES;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography level="h2" startDecorator={<AutoAwesomeIcon />}>
            Website Builder
          </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Generate AI-powered, SEO-optimized websites from your company data
            {projects && projects.length > 0 ? ` — ${projects.length} of ${MAX_WEBSITES} websites used` : ''}
          </Typography>
        </Box>
        <Tooltip
          title={atLimit ? `Limit reached: each account can have up to ${MAX_WEBSITES} websites. Delete one to create a new one.` : ''}
          disableHoverListener={!atLimit}
        >
          <span>
            <Button
              startDecorator={<AddIcon />}
              onClick={() => navigate('/marketing/website-builder/new')}
              size="lg"
              disabled={atLimit}
            >
              Create New Website
            </Button>
          </span>
        </Tooltip>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !projects || projects.length === 0 ? (
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'lg',
            p: 6,
            textAlign: 'center',
          }}
        >
          <LanguageIcon size={48} color="var(--joy-palette-neutral-400)" style={{ marginBottom: 16 }} />
          <Typography level="h3" sx={{ mb: 1 }}>
            No websites yet
          </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3 }}>
            Create your first AI-generated website in minutes using your company data.
          </Typography>
          <Button
            startDecorator={<AddIcon />}
            onClick={() => navigate('/marketing/website-builder/new')}
            size="lg"
          >
            Create Your Website
          </Button>
        </Sheet>
      ) : (
        <Grid container spacing={2}>
          {projects.map((project: WebsiteProject) => (
            <Grid key={project.id} xs={12} sm={6} md={4}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.500',
                    boxShadow: 'md',
                  },
                }}
                onClick={() => navigate(`/marketing/website-builder/${project.id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography level="title-lg" noWrap sx={{ flex: 1, mr: 1 }}>
                      {project.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={statusColors[project.status] || 'neutral'}
                      >
                        {statusLabels[project.status] || project.status}
                      </Chip>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={(e) => handleDelete(e, project.id, project.name)}
                        loading={deleteProject.isPending}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                    {project.pages_count} pages · {project.revision_count} revisions
                  </Typography>

                  {project.style_preferences?.layout_style && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                      <Chip size="sm" variant="outlined">
                        {project.style_preferences.layout_style}
                      </Chip>
                      {project.style_preferences.color_mood && (
                        <Chip size="sm" variant="outlined">
                          {project.style_preferences.color_mood}
                        </Chip>
                      )}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {project.production_url && (
                      <Button
                        size="sm"
                        variant="plain"
                        endDecorator={<OpenInNewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(project.production_url, '_blank');
                        }}
                      >
                        View Live Site
                      </Button>
                    )}
                    {['site_review', 'deployed'].includes(project.status) && (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/marketing/website-builder/${project.id}/manage`);
                        }}
                      >
                        Manage
                      </Button>
                    )}
                  </Box>

                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                    Created {new Date(project.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
